import useId from '@mui/utils/useId';
import { useController, useFormContext } from 'react-hook-form';
import { Input, Stack, ThemeProvider } from '@mui/material';
import { npiValid } from 'npi-validator';
import { useEffect } from 'react';

import FormErrorMessage from '@/components/FormErrorMessage';
import themeV2 from '@/themeV2';
import { executionRequirementShape } from '@/utils/transformFrontendCareGapAction';
import DynamicFormLabel from '@/components/DynamicForm/DynamicFormLabel';

const OTHER_PROVIDER_TRIGGER_VALUE = '';

export default function DynamicFormExternalNPI({ field }) {
  const { name } = field;
  const fieldId = useId();
  const { watch, setValue, clearErrors } = useFormContext();

  const servicingProviderId = watch('servicing_provider_id');

  const controller = useController({
    name,
    defaultValue: '',
    rules: {
      maxLength: {
        value: 10,
        message: 'NPI must be at most 10 characters',
      },
      required:
        servicingProviderId === OTHER_PROVIDER_TRIGGER_VALUE
          ? 'NPI is required when selecting Other provider.'
          : false,
      validate: (value) => {
        if (
          servicingProviderId === OTHER_PROVIDER_TRIGGER_VALUE &&
          value &&
          (!['1', '2'].includes(value.charAt(0)) || !npiValid(value))
        ) {
          return 'Invalid NPI format';
        }
        return true;
      },
    },
  });

  useEffect(() => {
    if (servicingProviderId !== OTHER_PROVIDER_TRIGGER_VALUE) {
      setValue(name, '');
      clearErrors(name);
    }
  }, [servicingProviderId, setValue, name, clearErrors]);

  return (
    servicingProviderId === OTHER_PROVIDER_TRIGGER_VALUE && (
      <ThemeProvider theme={themeV2}>
        <Stack marginTop={(theme) => theme.spacing(2)}>
          <DynamicFormLabel htmlFor={fieldId}>
            Other Provider NPI
          </DynamicFormLabel>
          <Input
            id={fieldId}
            inputProps={{ inputMode: 'numeric' }}
            disableUnderline
            sx={(theme) => ({
              boxShadow: 'None',
              width: 130,
              border: `1px solid ${theme.palette.border.input}`,
              '&:hover': {
                border: `2px solid ${theme.palette.primary.main}`,
                boxShadow: `0 0 0 3.2px ${theme.palette.focus.shadow}`,
              },
            })}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...controller.field}
          />
        </Stack>
        <FormErrorMessage errors={controller.formState.errors} name={name} />
      </ThemeProvider>
    )
  );
}

DynamicFormExternalNPI.propTypes = {
  field: executionRequirementShape.isRequired,
};
